import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * URL search params backed by a per-session store, so a view encoded in the
 * query string (filters, sort, active tag, …) survives leaving the page and
 * returning by ANY path — not just the browser back button.
 *
 * Storing view-state in the URL alone is fragile: in-app "Back" links commonly
 * navigate to the bare route (e.g. `/admin`, no query string), which silently
 * drops every filter on return. This hook closes that gap by OBSERVING the
 * params, so it works whether mutations flow through the returned `updateParams`
 * or through sibling hooks (e.g. `useStatusFilterParam`) that write the same URL.
 *
 * The URL stays the source of truth:
 *  - on every change it snapshots the `paramKeys` to `sessionStorage`;
 *  - on mount, only when the incoming URL carries none of `paramKeys`, it
 *    rehydrates the last snapshot into the URL (ref-guarded to run once, so a
 *    deliberate "clear all filters" isn't immediately undone).
 *
 * The empty snapshot is written only after a real view has existed this mount,
 * so a transient bare render — the initial mount, or React StrictMode's
 * double-invocation — never clobbers the stored view before restore reads it.
 * That also makes a genuine "clear" stick (stored as `""`, honored as cleared).
 *
 * @param storageKey  sessionStorage key — unique per persisted view.
 * @param paramKeys   query keys that constitute "the view". Their presence on
 *                    entry means the incoming URL is authoritative (e.g. a deep
 *                    link) and no restore happens. Pass a stable reference.
 * @returns `[searchParams, updateParams]`. `updateParams((p) => …)` mutates the
 *          URL (persistence is handled by the observer, not the setter), so the
 *          same snapshot is written no matter who changed the params.
 */
export function useSessionPersistedSearchParams(
  storageKey: string,
  paramKeys: readonly string[],
): [URLSearchParams, (mutate: (p: URLSearchParams) => void) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          mutate(p);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Canonical snapshot of just the view params, in a stable key order, so the
  // stored string is reproducible regardless of incidental URL key ordering or
  // unrelated params that may share the route.
  const snapshot = useMemo(() => {
    const p = new URLSearchParams();
    for (const key of paramKeys) {
      for (const value of searchParams.getAll(key)) p.append(key, value);
    }
    return p.toString();
  }, [searchParams, paramKeys]);

  // Restore once on a bare entry (e.g. an in-app back link to the param-less
  // route). A deep link that already carries view params is authoritative and
  // left untouched; the observer effect below persists it on its own.
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (paramKeys.some((k) => searchParams.has(k))) return; // incoming URL wins
    const saved = readSnapshot(storageKey);
    if (saved) setSearchParams(new URLSearchParams(saved), { replace: true });
  }, [searchParams, setSearchParams, storageKey, paramKeys]);

  // Persist on change. The empty snapshot is written only once a real view has
  // existed this mount, so an initial/transient bare render cannot wipe the
  // stored view before restore — while a real "clear" still persists as "".
  const everHadViewRef = useRef(false);
  useEffect(() => {
    if (snapshot) {
      everHadViewRef.current = true;
      writeSnapshot(storageKey, snapshot);
    } else if (everHadViewRef.current) {
      writeSnapshot(storageKey, '');
    }
  }, [snapshot, storageKey]);

  return [searchParams, updateParams];
}

function readSnapshot(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null; // storage unavailable (private mode / quota) — nothing to restore
  }
}

function writeSnapshot(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal */
  }
}
